import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  buildEffectiveGameConfig,
  hasLocalizationOverrides,
  rewriteComponentPathForLocalizationOverride,
  shouldUseLocalizationOverride
} from "../modules/localizationOverrides";

import { Card } from "@material-ui/core";
import { CardContent } from "@mui/material";
import _ from "lodash";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DialogPlayableSize from "./dialogPlayableSize";
import LocalizationOverrideLanguageCard from "./localizationOverrideLanguageCard";
import DefaultVersionSetting from "./defaultVersionSetting";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import OuterComponentIcon from "../../images/gameConfig/Outer-component-icon.svg";
import InnerComponentIcon from "../../images/gameConfig/Inner-component-icon.svg";
import { IconButton } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import VersionComponentSettingsEditors, {
  getTextWidth,
  renderTooltip,
} from "./versionComponentSettingsEditors";

const useStyles = makeStyles({
  card: { margin: "5px" },
  imageSelector: { width: "100%" },
  imageSetSelector: { width: "100%" },
  cardOrderSelector: { width: "100%" },
  containerVersionSettingsDiv: {
    margin: "0px",
    height: "100%",
    overflowY: "auto"
  }
});

function EndIcon(props) {
  return <DisabledByDefaultRoundedIcon {...props} sx={{ opacity: 0.3 }} />;
}

function getSiblingComponentsAndIndex(component, parent) {
  const componentPath = component.settings[0].path;
  const stringToCheck = componentPath.split("[")[0];
  let componentIndex;
  const siblingComponents = _.filter(parent.children, (e, index) => {
    const path = e.settings[0].path;
    if (path === componentPath) {
      componentIndex = index;
    }
    return _.startsWith(e.settings[0].path, stringToCheck);
  });
  return { siblingComponents, componentIndex };
}

/**
 * @param props.selectedVersion
 * @param props.selectedBase
 * @param props.selectedBase.gameObjectLabels
 * @param props.selectedBase.versionSettings
 * @param props.setSelectedVersionPathValue
 * @param props.setGameObjectKeyValue
 * @param props.setComponentPathValue
 * @param props.setKeyValue
 * @param props.resetGameConfig
 * @param props.media
 * @param props.areMediaLoaded
 * @param props.insertMediaPathValue
 * @param props.atlases
 * @param props.areAtlasesLoaded
 * @param props.insertAtlasPathValue
 * @param props.spines
 * @param props.areSpinesLoaded
 * @param props.insertSpinePathValue
 * @param props.sounds
 * @param props.areSoundsLoaded
 * @param props.insertSoundPathValue
 * @param props.fonts
 * @param props.areFontsLoaded
 * @param props.videos
 * @param props.areVideosLoaded
 * @param props.insertFontPathValue
 * @param props.classes
 * @param props.createSnackbar
 * @param props.deleteImageFromMedia
 * @param props.deleteImageSetFromMedia
 * @param props.deleteFontFromFonts
 * @param props.deleteSoundFromSounds
 * @param props.deleteVideoFromVideos
 * @param props.deleteAnimationSetFromAtlases
 * @param props.deleteAnimationSetFromSpines
 * @param props.deleteGlbFromGlbs
 * @param props.userPermissions
 * @param props.setIsDialogConfirmRefreshOpen
 * @param props.setIsDialogCircularProgressOpen
 */
export default function containerVersionSettingsTreeView(props) {
  if (
    !_.has(props, "selectedBase") ||
    _.get(props, "areMediaLoaded") !== true ||
    _.get(props, "areAtlasesLoaded") !== true ||
    _.get(props, "areSpinesLoaded") !== true ||
    _.get(props, "areSoundsLoaded") !== true ||
    _.get(props, "areFontsLoaded") !== true ||
    _.get(props, "areFontsLoaded") !== true
  ) {
    return null;
  }
  const classes = useStyles();

  const realSelectedVersion = props.selectedVersion;
  const realSetSelectedVersionPathValue = props.setSelectedVersionPathValue;
  const selectedLanguage =
    props.selectedLanguage || _.get(realSelectedVersion, "gameConfig.options.language", "en");

  const selectedVersionForEditors = useMemo(() => {
    const gameConfig = _.get(realSelectedVersion, "gameConfig");
    if (!shouldUseLocalizationOverride(gameConfig, selectedLanguage)) {
      return realSelectedVersion;
    }
    return {
      ...realSelectedVersion,
      gameConfig: buildEffectiveGameConfig(gameConfig, selectedLanguage)
    };
  }, [realSelectedVersion, selectedLanguage]);

  const setSelectedVersionPathValue = useCallback(
    (p, disableUndo) => {
      realSetSelectedVersionPathValue(
        rewriteComponentPathForLocalizationOverride(
          p,
          _.get(realSelectedVersion, "gameConfig"),
          selectedLanguage
        ),
        disableUndo
      );
    },
    [realSelectedVersion, realSetSelectedVersionPathValue, selectedLanguage]
  );

  // Editors read effective values; writes route through the rewriter above.
  props = {
    ...props,
    selectedVersion: selectedVersionForEditors,
    setSelectedVersionPathValue
  };

  let [sections, setSections] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [refresh, doRefresh] = useState(0);
  const [firstTime, setFirstTime] = useState(false);

  const [expandedItems, setExpandedItems] = useState([]);

  const handleToggle = (event, itemIds) => {
    setExpandedItems(itemIds); // Set the expanded item IDs (array)
  };
  const setValue = (p, disableUndo = false) => {
    props.setSelectedVersionPathValue(p, disableUndo);
  };

  useEffect(() => {
    doRefresh((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (_.isNil(props.selectedModifiedBase)) return;
    // console.log("modified base", props.selectedModifiedBase);
    const cloneComponents = _.cloneDeep(_.get(props.selectedModifiedBase.versionSettings, "components", []));
    const sections = [];
    cloneComponents.forEach((component) => {
      const sectionText = _.get(component, "section", "Default");
      const subSectionText = _.get(component, "subSection", null);

      if (_.isNil(component.parentKey)) {
        let index;
        component.colorIndex = 1;
        if (sections.length === 0) {
          const obj = { caption: sectionText, components: [] };
          if (!_.isNil(subSectionText)) {
            obj.subSections = [{ caption: subSectionText, children: [] }];
          }
          sections.push(obj);
          index = 0;
        } else {
          index = _.findIndex(sections, (e) => {
            return e.caption === sectionText;
          });
          if (index < 0) {
            const obj = { caption: sectionText, components: [] };
            if (!_.isNil(subSectionText)) {
              obj.subSections = [{ caption: subSectionText, children: [] }];
            }
            sections.push(obj);
            index = sections.length - 1;
          }
        }
        if (index > -1) {
          if (!_.isNil(sections[index].subSections)) {
            const subIndex = _.findIndex(sections[index].subSections, (e) => {
              return e.caption === subSectionText;
            });
            if (subIndex < 0) {
              sections[index].subSections.push({ caption: subSectionText, children: [component] });
            } else {
              sections[index].subSections[subIndex].children.push(component);
            }
          } else {
            sections[index].components.push(component);
          }
        }
      } else {
        const parentComponent = _.find(cloneComponents, (e) => {
          if (component.isGroup) {
            return e.key === component.parentKey && e.id === component.id && e.levelIndex === component.levelIndex;
          }
          else if (component.isSubGroup) {
            return e.isGroup
              && e.id === component.id
              && e.levelIndex === component.levelIndex
              && e.caption === component.parentGroup;
          }
          else if (!_.isNil(component.parentId)) {
            return e.key === component.parentKey && e.id === component.parentId
              && (_.isNil(component.levelIndex) || _.isNil(e.levelIndex));
          }
          else {
            return e.key === component.parentKey
              && (_.isNil(component.levelIndex) || _.isNil(e.levelIndex));
          }
        });

        component.colorIndex = parentComponent.colorIndex + 1;

        if (!_.isNil(subSectionText)) {
          if (!parentComponent.childSubSections) {
            parentComponent.childSubSections = [];
          }
          const subIndex = _.findIndex(parentComponent.childSubSections, (e) => e.caption === subSectionText);
          if (subIndex < 0) {
            parentComponent.childSubSections.push({ caption: subSectionText, children: [component], colorIndex: component.colorIndex });
          } else {
            parentComponent.childSubSections[subIndex].children.push(component);
          }
        } else {
          parentComponent.children.push(component);
        }
      }
    });
    // console.log("enters here components", cloneComponents, sections);
    setSections(sections);
  }, [props.selectedModifiedBase]);

  let baseIDSession = JSON.parse(localStorage.getItem("selectedBaseSession"));
  if (baseIDSession === _.get(props, "selectedBase._id")) {
    localStorage.setItem("selectedBaseSession", JSON.stringify(_.get(props, "selectedBase._id")));

    if (!firstTime) {
      setFirstTime(true);
    }
  } else {
    localStorage.setItem("newgetVal", JSON.stringify([]));
  }

  const defaultCardColor = "#474747";
  const cardColors = ["#313131", "#474747", "#545454", "#656565", "#585858"];

  function getCardColor(index) {
    return cardColors[index >= cardColors.length ? cardColors.length - 1 : index];
  }



  function onAddIconClicked(parent, component) {
    // console.log("enters here onAddIconClicked", parent, component);
    const array = props.selectedVersion.gameConfig.components[component.id][component.settings[0].path.split("[")[0]];
    array.push(array[array.length - 1]);
    setValue(
      {
        path: `gameConfig.components[${component.id}][${component.settings[0].path.split("[")[0]}]`,
        value: array
      },
      true
    );
    props.setSelectedModifiedBase(null);
  }

  function onRemoveIconClicked(event, parent, component) {
    // eslint-disable-next-line no-unused-vars
    const { siblingComponents, componentIndex } = getSiblingComponentsAndIndex(component, parent);
    const array = props.selectedVersion.gameConfig.components[component.id][component.settings[0].path.split("[")[0]];
    array.splice(componentIndex, 1);
    setValue(
      {
        path: `gameConfig.components[${component.id}][${component.settings[0].path.split("[")[0]}]`,
        value: array
      },
      true
    );
    props.setSelectedModifiedBase(null);
  }

  function shouldAddIconBeVisible(parent, component) {
    const { siblingComponents, componentIndex } = getSiblingComponentsAndIndex(component, parent);
    const siblingComponentCount = siblingComponents.length;
    // console.log({ componentIndex, siblingComponentCount });
    if (component.maxCount <= siblingComponentCount) return false;
    return componentIndex === siblingComponentCount - 1;
  }

  function shouldRemoveIconBeVisible(parent, component) {
    const { siblingComponents } = getSiblingComponentsAndIndex(component, parent);
    const siblingComponentCount = siblingComponents.length;
    // console.log({ siblingComponentCount, minCount: component.minCount });
    return component.minCount < siblingComponentCount;
  }

  function renderTreeView(
    parent,
    component,
    props,
    classes,
    getTextWidth,
    renderTooltip,
    setValue,
    isSubSection = false,
    parentItemId = ""
  ) {
    if (component
      && (component.settings && component.settings.length === 0)
      && (component.children && component.children.length === 0)
    ) {
      return null;
    }
    // The id has to carry the ANCESTRY, not just the node. One component id can
    // appear under two different parents — two board objects each holding a
    // layer called "1" — and MUI's tree refuses to render duplicate ids at all:
    // it throws, and the whole page goes white (measured on wow-zen-search with
    // two objects expanded at once).
    const ownItemId = `${component.caption}_${component.id}${
      !_.isNil(component.levelIndex) ? `_level${component.levelIndex}` : ""
    }`;
    const itemId = parentItemId ? `${parentItemId}/${ownItemId}` : ownItemId;

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <div style={{ display: "flex", alignItems: "center", minHeight: "32px" }}>
            <img
              src={isSubSection ? OuterComponentIcon : InnerComponentIcon}
              alt="custom icon"
              style={{ width: "18px", height: "18px", marginRight: "8px" }}
            />
            {component.caption}
            {component.isBelongToArray && (
              <div
                style={{
                  "display": "flex",
                  "justifyContent": "space-between",
                  "marginLeft": "auto"
                }}
              >
                {shouldRemoveIconBeVisible(parent, component) && (
                  <IconButton
                    disableRipple={true}
                    style={{ background: "none", color: "#949494", padding: "0px" }}
                    onClick={(event) => {
                      if (props.userPermissions["editContent"] === false) {
                        props.createSnackbar(
                          "You do not have permission to update, contact your administrator.",
                          "warning",
                          false
                        );
                        return;
                      }
                      event.preventDefault();
                      event.stopPropagation(); // Prevents the parent from being clicked
                      onRemoveIconClicked(event, parent, component);
                    }}
                  >
                    <Remove />
                  </IconButton>
                )}
                {shouldAddIconBeVisible(parent, component) && (
                  <IconButton
                    disableRipple={true}
                    style={{ background: "none", color: "#949494", padding: "0px" }}
                    onClick={(event) => {
                      if (props.userPermissions["editContent"] === false) {
                        props.createSnackbar(
                          "You do not have permission to update, contact your administrator.",
                          "warning",
                          false
                        );
                        return;
                      }
                      event.preventDefault();
                      event.stopPropagation(); // Prevents the parent from being clicked
                      onAddIconClicked(parent, component);
                    }}
                  >
                    <Add />
                  </IconButton>
                )}
              </div>
            )}
          </div>
        }
        style={{
          backgroundColor: getCardColor(component.colorIndex),
          marginTop: "5px",
          marginBottom: "5px",
          borderRadius: "4px"
        }}
      >
        <Card
          className={props.classes.card}
          key={`${component.id}-${component.caption.replace(/\s/g, "")}`}
          style={{
            backgroundColor: isSubSection ? "#ffffff00" : getCardColor(component.colorIndex),
            boxShadow: "none"
          }}
        >
          <CardContent
            sx={{
              "&:last-child": {
                paddingBottom: isSubSection ? "0px" : "24px"
              },
              padding: isSubSection ? "0px" : "16px"
            }}
          >
            <VersionComponentSettingsEditors
              component={component}
              props={props}
              classes={classes}
              setValue={setValue}
              realSelectedVersion={realSelectedVersion}
            />
            {_.get(component.children, "length") > 0
              ? (() => {
                const levelSelectorSetting = _.find(component.settings, (s) => s.editorType === "selectorLevelType");
                let filteredChildren = component.children;

                if (levelSelectorSetting) {
                  // If linkedLevelPath is set, read level from the linked component instead
                  const levelPath = levelSelectorSetting.linkedLevelPath
                    ? `selectedVersion.gameConfig.components.${levelSelectorSetting.linkedLevelPath}`
                    : `selectedVersion.gameConfig.components.${component.id}.${levelSelectorSetting.path}`;
                  const selectedLevel = _.get(
                    props,
                    levelPath,
                    _.get(levelSelectorSetting, "items[0]", "Level 1")
                  );
                  const selectedLevelIndex = levelSelectorSetting.items.indexOf(selectedLevel);
                  filteredChildren = component.children.filter((child) => {
                    if (_.isNil(child.levelIndex)) return true;
                    return child.levelIndex === selectedLevelIndex;
                  });
                }

                return filteredChildren.map((child) => {
                  if (isSubSection) {
                    return (
                      <Card
                        className={props.classes.card}
                        key={`${child.id}-${child.caption.replace(/\s/g, "")}`}
                        style={{
                          overflow: "unset"
                        }}
                      >
                        {renderTreeView(
                          component,
                          child,
                          props,
                          classes,
                          getTextWidth,
                          renderTooltip,
                          setValue,
                          false,
                          itemId
                        )}
                      </Card>
                    );
                  } else {
                    return renderTreeView(
                      component,
                      child,
                      props,
                      classes,
                      getTextWidth,
                      renderTooltip,
                      setValue,
                      false,
                      itemId
                    );
                  }
                });
              })()
              : null}
            {_.get(component.childSubSections, "length") > 0
              ? component.childSubSections.map((subSection) => {
                return renderTreeView(
                  component,
                  subSection,
                  props,
                  classes,
                  getTextWidth,
                  renderTooltip,
                  setValue,
                  true,
                  itemId
                );
              })
              : null}
          </CardContent>
        </Card>
      </TreeItem>
    );
  }
  return (
    <div className={classes.containerVersionSettingsDiv}>
      <div>
        <DialogPlayableSize
          classes={props.classes}
          selectedVersion={realSelectedVersion}
          selectedBase={props.selectedBase}
          setTotalSize={props.setTotalSize}
          assetEnabledByComponent={_.get(props, "selectedModifiedBase.assetEnabledByComponent", {})}
          selectedLanguage={props.selectedLanguage}
        />
        {hasLocalizationOverrides(_.get(realSelectedVersion, "gameConfig")) && (
          <LocalizationOverrideLanguageCard
            classes={props.classes}
            selectedLanguage={props.selectedLanguage}
            supportedLanguages={_.get(props, "selectedBase.supportedLanguages", {})}
            onNavigateToLocalization={props.onNavigateToLocalization}
          />
        )}
        <SimpleTreeView
          aria-label="customized"
          defaultExpandedItems={["1", "3"]}
          slots={{
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDropDownIcon,
            endIcon: EndIcon
          }}
          sx={{ overflowX: "hidden", flexGrow: 1 }}
          expandedItems={expandedItems}
          onExpandedItemsChange={handleToggle}
        >
          {!_.isNil(sections) &&
            sections.map((section) => {
              return (
                <Card
                  className={props.classes.card}
                  key={section.caption}
                  style={{
                    backgroundColor:
                      _.findIndex(expandedItems, (e) => {
                        return e === section.caption;
                      }) > -1
                        ? getCardColor(0)
                        : defaultCardColor
                  }}
                >
                  <TreeItem
                    itemId={section.caption}
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontWeight: "bold",
                          minHeight: "32px"
                        }}
                      >
                        <img
                          src={OuterComponentIcon}
                          alt="custom icon"
                          style={{ width: "18px", height: "18px", marginRight: "8px" }}
                        />
                        {section.caption}
                      </div>
                    }
                  >
                    {_.get(section.subSections, "length") > 0
                      ? section.subSections.map((subSection) => {
                        return renderTreeView(
                          null,
                          subSection,
                          props,
                          classes,
                          getTextWidth,
                          renderTooltip,
                          setValue,
                          true,
                          section.caption
                        );
                      })
                      : section.components
                        .filter((component) => {
                          if (!component.linkedLevelSelector || _.isNil(component.levelIndex)) return true;
                          const selectedLevel = _.get(
                            props,
                            `selectedVersion.gameConfig.components.${component.linkedLevelSelector.path}`,
                            _.get(component.linkedLevelSelector, "items[0]", "Level 1")
                          );
                          const selectedLevelIndex = component.linkedLevelSelector.items.indexOf(selectedLevel);
                          return component.levelIndex === selectedLevelIndex;
                        })
                        .map((component) => {
                          return renderTreeView(
                            null,
                            component,
                            props,
                            classes,
                            getTextWidth,
                            renderTooltip,
                            setValue,
                            false,
                            section.caption
                          );
                        })}
                  </TreeItem>
                </Card>
              );
            })}
        </SimpleTreeView>
        {_.has(props.userCompany, "isPermittedSetDefault") && props.userCompany.isPermittedSetDefault === true && (
          <DefaultVersionSetting
            classes={props.classes}
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            getDefaultVersion={props.getDefaultVersion}
            setDefaultVersion={(value) => {
              props.setDefaultVersion(value);
            }}
            setSearchBarValue={(value) => {
              props.setSearchBarValue(value);
            }}
            defaultVersion={props.defaultVersion}
            defaultVersionUpdated={props.defaultVersionUpdated}
            setDefaultVersionUpdated={(value) => {
              props.setDefaultVersionUpdated(value);
            }}
            prop={props}
            versionRename={props.versionRename}
            searchBarValue={props.searchBarValue}
          />
        )}
      </div>
    </div>
  );
}
